import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Shield,
  Crown,
  Eye,
  GitCompare,
  Copy,
  Download,
  Code,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Play,
  Filter,
  Info,
  X,
  Loader2,
  Cloud,
  Wand2,
  ShipWheel
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { apiClient, type Cluster, type IaCRepoConfig } from '@/api/client';
import { ReactComponent as NamespaceIcon } from '../assets/icons/namespace.svg';
import { DeployToGitHubButton } from '@/components/github';
import { GitPullRequest, ExternalLink } from 'lucide-react';
import { getLanguageBadge, DiffView } from './shared/codeDiffUtils';

// Interfaces for Chat YAML Changes
interface ChatYAMLChange {
  id: string;
  tenant_id: string;
  user_id: string;
  cluster_id: string;
  namespace_id?: string;
  file_path: string;
  file_type: 'create' | 'update' | 'delete';
  yaml_content: string;
  existing_yaml?: string;
  explanation?: string;
  resource_name?: string;
  resource_kind?: string;
  resource_namespace?: string;
  namespace_owner_approved_by?: string;
  namespace_owner_approved_at?: number;
  ops_admin_approved_by?: string;
  ops_admin_approved_at?: number;
  org_admin_approved_by?: string;
  org_admin_approved_at?: number;
  applied_at?: number;
  applied_by?: string;
  application_status?: 'pending' | 'applied' | 'failed' | 'pr_created';
  application_error?: string;
  pr_url?: string;
  chat_message_id: string;
  user_query: string;
  created_at: number;
  updated_at: number;
  cluster_name?: string;
  user_email?: string;
  user_role?: string;

  // Dynamic approval status (computed server-side)
  requires_ops_admin_approval?: boolean;
  can_user_approve?: boolean;
  approval_status?: string;
}

// Interface for Cloud Changes (Terraform, CloudFormation, AWS CLI)
interface ChatCloudChange {
  id: string;
  change_type: 'terraform' | 'cloudformation' | 'pulumi' | 'aws_cli' | 'oci_cli';
  content: string;
  existing_content?: string; // Original content before changes (for diff view)
  resource_type?: string;
  resource_identifier?: string;
  explanation?: string;
  file_type?: string;
  file_path?: string;
  application_status?: string;
  pr_url?: string; // URL of the created PR (for IaC changes)
  aws_connection_id?: string;
  aws_account_id?: string;
  user_email?: string;
  chat_message_id?: string;
  created_at: string;
  updated_at: string;
}

// Interface for Code Changes (source code fixes from connected repos)
interface ChatCodeChange {
  id: string;
  repo_platform: string;
  repo_full_name: string;
  github_repository_id?: string;
  gitlab_repository_id?: string;
  file_path: string;
  line_start: number;
  line_end: number;
  language?: string;
  original_code: string;
  fixed_code: string;
  explanation?: string;
  pr_url?: string;
  pr_status?: string;
  application_status?: string;
  chat_message_id?: string;
  user_query?: string;
  user_email?: string;
  created_at: string;
  updated_at: string;
}

// Grouped code changes — changes from the same chat message are grouped together
interface CodeChangeGroup {
  groupKey: string; // chat_message_id or fallback to individual change id
  chat_message_id?: string;
  repo_platform: string;
  repo_full_name: string;
  changes: ChatCodeChange[];
  user_query?: string;
  user_email?: string;
  created_at: string;
  // PR status: all changes in the group share the same PR
  application_status?: string;
  pr_url?: string;
}

interface NamespaceGroup {
  namespace: string;
  changes: ChatYAMLChange[];
  isExpanded: boolean;
}


const ChatChangelog: React.FC = () => {
  const [changes, setChanges] = useState<ChatYAMLChange[]>([]);
  const [cloudChanges, setCloudChanges] = useState<ChatCloudChange[]>([]);
  const [codeChanges, setCodeChanges] = useState<ChatCodeChange[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clustersLoading, setClustersLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'my-reviews'>('all');
  const [changeType, setChangeType] = useState<'kubernetes' | 'cloud' | 'code'>('kubernetes');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;
  const [creatingCodePR, setCreatingCodePR] = useState<string | null>(null);
  const [selectedClusterId, setSelectedClusterId] = useState<string>('all');
  const [expandedChanges, setExpandedChanges] = useState<Set<string>>(new Set());
  const [showDiffView, setShowDiffView] = useState<Set<string>>(new Set());
  const [copiedChange, setCopiedChange] = useState<string | null>(null);
  const [namespaceGroups, setNamespaceGroups] = useState<NamespaceGroup[]>([]);
  const [applyingChangeId, setApplyingChangeId] = useState<string | null>(null);
  const [applyingCloudChangeId, setApplyingCloudChangeId] = useState<string | null>(null);
  const [cloudApplyResult, setCloudApplyResult] = useState<{
    changeId: string;
    success: boolean;
    message: string;
    output?: string;
    error_message?: string;
    fix_attempted?: boolean;
    fixed_command?: string;
    fix_explanation?: string;
    fix_changes_made?: string[];
    cannot_auto_fix?: boolean;
  } | null>(null);

  // IaC state for Deploy IaC button
  const [iacRepos, setIacRepos] = useState<IaCRepoConfig[]>([]);
  const [creatingPR, setCreatingPR] = useState<string | null>(null);
  const [prResult, setPrResult] = useState<{
    changeId: string;
    success: boolean;
    pr_url?: string;
    error?: string;
  } | null>(null);

  useEffect(() => {
    loadClusters();
    loadIaCRepos();
  }, []);

  useEffect(() => {
    loadChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedClusterId]);

  useEffect(() => {
    if (changeType === 'cloud') {
      loadCloudChanges();
    } else if (changeType === 'code') {
      loadCodeChanges();
    }
  }, [changeType]);

  // Group code changes by chat_message_id + repo — changes from the same AI response
  // for the same repo are grouped as one changelog entry for multi-file PRs
  const codeChangeGroups: CodeChangeGroup[] = useMemo(() => {
    const groupMap = new Map<string, ChatCodeChange[]>();
    for (const change of codeChanges) {
      // Group by chat_message_id + repo_full_name; ungrouped changes get their own entry
      const key = change.chat_message_id
        ? `${change.chat_message_id}::${change.repo_full_name}`
        : `single::${change.id}`;
      const existing = groupMap.get(key) || [];
      existing.push(change);
      groupMap.set(key, existing);
    }

    return Array.from(groupMap.entries()).map(([key, changes]) => {
      const first = changes[0];
      // Use the most advanced status from any change in the group
      const hasPR = changes.some(c => c.application_status === 'pr_created');
      const prUrl = changes.find(c => c.pr_url)?.pr_url;
      return {
        groupKey: key,
        chat_message_id: first.chat_message_id,
        repo_platform: first.repo_platform,
        repo_full_name: first.repo_full_name,
        changes,
        user_query: first.user_query,
        user_email: first.user_email,
        created_at: first.created_at,
        application_status: hasPR ? 'pr_created' : first.application_status,
        pr_url: prUrl,
      };
    });
  }, [codeChanges]);

  const loadCloudChanges = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.listChatCloudChanges({ page_size: 50 });
      setCloudChanges(response.cloud_changes || []);
    } catch (err: any) {
      console.error('Failed to load cloud changes:', err);
      setError(err.message || 'Failed to load cloud changes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCodeChanges = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.listChatCodeChanges({ page_size: 50 });
      setCodeChanges(response.changes || []);
    } catch (err: any) {
      console.error('Failed to load code changes:', err);
      setError(err.message || 'Failed to load code changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCodeFixPR = async (group: CodeChangeGroup) => {
    setCreatingCodePR(group.groupKey);
    try {
      const fixes = group.changes.map(change => ({
        file_path: change.file_path,
        fixed_content: change.fixed_code,
        original_content: change.original_code,
        line_start: change.line_start,
        line_end: change.line_end,
      }));

      // PR title: single file uses filename, multi-file uses count
      const prTitle = group.changes.length === 1
        ? `fix: ${group.changes[0].file_path.split('/').pop() || group.changes[0].file_path}`
        : `fix: ${group.changes.length} files in ${group.repo_full_name.split('/').pop()}`;

      // PR description: list all files being changed
      const fileList = group.changes.map(c =>
        `- \`${c.file_path}\` (lines ${c.line_start}-${c.line_end})${c.explanation ? `: ${c.explanation}` : ''}`
      ).join('\n');
      const prDescription = `## Code Fix\n\n**Repository:** ${group.repo_full_name}\n**Files changed:** ${group.changes.length}\n\n${fileList}\n\nGenerated by Kestrel Cloud AI Chat`;

      const first = group.changes[0];
      const repositoryId = first.github_repository_id || first.gitlab_repository_id || '';
      // Pass all change IDs so the server can update all of them with the PR URL
      const sourceId = group.changes.map(c => c.id).join(',');

      let result;
      if (group.repo_platform === 'gitlab') {
        result = await apiClient.createCodeFixMergeRequest({
          incident_id: '',
          repository_id: repositoryId,
          repo_full_name: group.repo_full_name,
          fixes,
          pr_title: prTitle,
          pr_description: prDescription,
          source_type: 'code_fix',
          source_id: sourceId,
        });
        if (result.mr_url) {
          window.open(result.mr_url, '_blank');
        }
      } else {
        result = await apiClient.createCodeFixPullRequest({
          incident_id: '',
          repository_id: repositoryId,
          repo_full_name: group.repo_full_name,
          fixes,
          pr_title: prTitle,
          pr_description: prDescription,
          source_type: 'code_fix',
          source_id: sourceId,
        });
        if (result.pr_url) {
          window.open(result.pr_url, '_blank');
        }
      }

      loadCodeChanges();
    } catch (err: any) {
      console.error('Failed to create PR for code fix:', err);
      setError(err.message || 'Failed to create pull request');
    } finally {
      setCreatingCodePR(null);
    }
  };

  const loadIaCRepos = async () => {
    try {
      const response = await apiClient.listIaCRepos();
      setIacRepos(response.iac_repos || []);
    } catch (err) {
      // IaC not configured - that's fine
      console.log('IaC repos not available');
    }
  };

  const handleCreateCloudChangePR = async (changeId: string) => {
    if (iacRepos.length === 0) {
      setError('No IaC repository configured. Configure one in CI/CD Integration settings.');
      return;
    }

    setCreatingPR(changeId);
    setPrResult(null);

    try {
      // Find the matching IaC repo based on the change's file_path
      // file_path is like "kestrel-ai/rds-no-encryption/main.tf" and repo_full_name is "kestrel-ai/rds-no-encryption"
      const change = cloudChanges.find(c => c.id === changeId);
      let iacRepo = iacRepos[0]; // fallback to first repo
      if (change?.file_path) {
        const matchingRepo = iacRepos.find(r => change.file_path!.startsWith(r.repo_full_name + '/'));
        if (matchingRepo) {
          iacRepo = matchingRepo;
        }
      }
      const result = await apiClient.createCloudChangePR({
        change_id: changeId,
        repository_id: iacRepo.repository_id,
      });

      setPrResult({
        changeId,
        success: result.success,
        pr_url: result.pr_url,
        error: result.error,
      });

      if (result.success && result.pr_url) {
        // Reload cloud changes to update status
        loadCloudChanges();
        // Open PR in new tab
        window.open(result.pr_url, '_blank');
      }
    } catch (err: any) {
      console.error('Failed to create PR:', err);
      setPrResult({
        changeId,
        success: false,
        error: err.response?.data?.message || err.message || 'Failed to create pull request',
      });
    } finally {
      setCreatingPR(null);
    }
  };

  const loadClusters = async () => {
    setClustersLoading(true);
    try {
      const response = await apiClient.listClusters();
      setClusters(response.clusters || []);
    } catch (err: any) {
      console.error('Error loading clusters:', err);
      // Don't show error for clusters as it's not critical
    } finally {
      setClustersLoading(false);
    }
  };



  const groupChangesByNamespace = (changes: ChatYAMLChange[]) => {
    const groups = new Map<string, ChatYAMLChange[]>();
    
    changes.forEach(change => {
      const namespace = change.resource_namespace || 'cluster-scoped';
      if (!groups.has(namespace)) {
        groups.set(namespace, []);
      }
      groups.get(namespace)!.push(change);
    });

    const namespaceGroups: NamespaceGroup[] = Array.from(groups.entries())
      .map(([namespace, changes]) => ({
        namespace,
        changes: changes.sort((a, b) => b.created_at - a.created_at), // Sort by creation date, newest first
        isExpanded: true // Start with all expanded
      }))
      .sort((a, b) => a.namespace.localeCompare(b.namespace));

    setNamespaceGroups(namespaceGroups);
  };

  const loadChanges = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.listChatYAMLChanges({
        my_reviews_only: activeTab === 'my-reviews',
        page_size: 50
      });
      const allChanges = response.changes || [];
      
      // Filter by cluster if specific cluster is selected
      const filteredChanges = selectedClusterId === 'all' 
        ? allChanges 
        : allChanges.filter((change: ChatYAMLChange) => change.cluster_id === selectedClusterId);
      
      setChanges(filteredChanges);
      
      // Group by namespace if a specific cluster is selected
      if (selectedClusterId !== 'all') {
        groupChangesByNamespace(filteredChanges);
      } else {
        setNamespaceGroups([]);
      }
    } catch (err: any) {
      console.error('Failed to load configuration changes:', err);
      setError(err.message || 'Failed to load configuration changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (changeId: string) => {
    try {
      await apiClient.approveChatYAMLChange({ change_id: changeId });
      // Reload changes to reflect the approval
      loadChanges();
    } catch (err: any) {
      console.error('Failed to approve change:', err);
      setError(err.message || 'Failed to approve change');
    }
  };

  const handleApply = async (changeId: string) => {
    try {
      setApplyingChangeId(changeId);
      const response = await apiClient.applyChatYAMLChange({ change_id: changeId });
      if (response.success) {
        // Reload changes to reflect the application
        loadChanges();
      } else {
        setError(response.error_message || 'Failed to apply change');
      }
    } catch (err: any) {
      console.error('Failed to apply change:', err);
      setError(err.message || 'Failed to apply change');
    } finally {
      setApplyingChangeId(null);
    }
  };

  const handleApplyCloudChange = async (changeId: string) => {
    try {
      setApplyingCloudChangeId(changeId);
      setCloudApplyResult(null);
      const response = await apiClient.applyChatCloudChange({ change_id: changeId });
      
      setCloudApplyResult({
        changeId,
        success: response.success,
        message: response.message,
        output: response.output,
        error_message: response.error_message,
        fix_attempted: response.fix_attempted,
        fixed_command: response.fixed_command,
        fix_explanation: response.fix_explanation,
        fix_changes_made: response.fix_changes_made,
        cannot_auto_fix: response.cannot_auto_fix,
      });
      
      if (response.success) {
        // Reload to reflect the status change
        loadCloudChanges();
      }
    } catch (err: any) {
      console.error('Failed to apply cloud change:', err);
      setCloudApplyResult({
        changeId,
        success: false,
        message: 'Failed to apply change',
        error_message: err.message || 'Unknown error',
      });
    } finally {
      setApplyingCloudChangeId(null);
    }
  };

  const toggleChangeExpansion = (changeId: string) => {
    setExpandedChanges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(changeId)) {
        newSet.delete(changeId);
      } else {
        newSet.add(changeId);
      }
      return newSet;
    });
  };

  const toggleDiffView = (changeId: string) => {
    setShowDiffView(prev => {
      const newSet = new Set(prev);
      if (newSet.has(changeId)) {
        newSet.delete(changeId);
      } else {
        newSet.add(changeId);
      }
      return newSet;
    });
  };

  const toggleNamespaceExpansion = (namespace: string) => {
    setNamespaceGroups(prev => 
      prev.map(group => 
        group.namespace === namespace 
          ? { ...group, isExpanded: !group.isExpanded }
          : group
      )
    );
  };

  const handleClusterChange = (newCluster: string) => {
    setSelectedClusterId(newCluster);
    setPage(1);
  };

  const copyYamlToClipboard = async (yamlContent: string, changeId: string) => {
    try {
      await navigator.clipboard.writeText(yamlContent);
      setCopiedChange(changeId);
      setTimeout(() => setCopiedChange(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const downloadYamlFile = (yamlContent: string, filePath: string) => {
    const blob = new Blob([yamlContent], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const filename = filePath.split('/').pop() || 'change.yaml';
    link.setAttribute('download', filename);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getFileTypeBadge = (fileType: string) => {
    switch (fileType) {
      case 'create': return <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">Create</Badge>;
      case 'update': return <Badge className="bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800">Update</Badge>;
      case 'delete': return <Badge className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">Delete</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const getFileTypeIcon = (_fileType: string) => {
    return null;
  };

  const getApprovalStatus = (change: ChatYAMLChange) => {
    // Use server-computed approval status if available
    const status = change.approval_status || 'pending';
    
    switch (status) {
      case 'failed':
        return { status: 'failed', color: 'text-red-600', icon: <XCircle className="h-4 w-4" /> };
      case 'applied':
        return { status: 'applied', color: 'text-green-600', icon: <CheckCircle className="h-4 w-4" /> };
      case 'ready_to_apply':
        return { status: 'Ready to Apply', color: 'text-green-600', icon: <CheckCircle className="h-4 w-4" /> };
      case 'approved_no_safe_apply':
        return { status: 'Approved (Safe-Apply Disabled)', color: 'text-orange-600', icon: <AlertTriangle className="h-4 w-4" /> };
      case 'approved':
      case 'fully_approved':
        return { status, color: 'text-green-600', icon: <CheckCircle className="h-4 w-4" /> };
      case 'pending_security':
        return { status: 'Pending Platform Admin', color: 'text-muted-foreground', icon: <Clock className="h-4 w-4" /> };
      case 'pending_namespace':
        return { status: 'Pending Namespace Owner', color: 'text-yellow-600', icon: <Clock className="h-4 w-4" /> };
      default:
        return { status: 'pending', color: 'text-gray-600', icon: <Clock className="h-4 w-4" /> };
    }
  };

  const canUserApprove = (change: ChatYAMLChange) => {
    // Don't show approve button if change is already applied or failed
    if (change.application_status === 'applied' || change.application_status === 'failed') {
      return false;
    }
    
    // Use server-computed permission check
    return change.can_user_approve || false;
  };

  const canSafeApply = (change: ChatYAMLChange) => {
    // Only allow safe-apply if the server says it's ready and cluster allows safe-apply
    const status = change.approval_status || 'pending';
    return status === 'ready_to_apply' &&
           change.application_status !== 'applied' && 
           change.application_status !== 'failed';
  };

  // Get selected cluster name for display
  const selectedClusterName = useMemo(() => {
    if (selectedClusterId === 'all') return 'All Clusters';
    const cluster = clusters.find(c => c.id === selectedClusterId);
    return cluster ? cluster.cluster_name : 'Unknown Cluster';
  }, [selectedClusterId, clusters]);

  // Statistics for display
  const stats = useMemo(() => {
    const total = changes.length;
    const namespaceCount = new Set(changes.map(c => c.resource_namespace).filter(Boolean)).size;
    
    return {
      total,
      namespaces: namespaceCount
    };
  }, [changes]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRole = (role: string) => {
    if (role === 'ops_admin') return 'Platform Admin';
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getRoleIcon = (_role: string) => {
    return null;
  };

  const getRoleBadgeColor = (_role: string) => {
    return 'bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-200 dark:border-zinc-800/30';
  };

  // Render individual change card
  const renderChangeCard = (change: ChatYAMLChange, borderColor: string = 'border-l-zinc-400') => {
    const isExpanded = expandedChanges.has(change.id);
    const isCopied = copiedChange === change.id;
    const approvalStatus = getApprovalStatus(change);

    return (
      <Card key={change.id} className={`border-l-4 ${borderColor}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-base font-mono">{change.file_path}</CardTitle>
                {getFileTypeBadge(change.file_type)}
                <Badge variant="outline" className={approvalStatus.color}>
                  {approvalStatus.icon}
                  <span className="ml-1 capitalize">{approvalStatus.status.replace('_', ' ')}</span>
                </Badge>
              </div>
              {change.resource_name && (
                <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                  <code className="text-xs font-mono">{change.resource_kind}/{change.resource_name}</code>
                  {change.resource_namespace && (
                    <>
                      <span>in</span>
                      <code className="text-xs font-mono">{change.resource_namespace}</code>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {change.application_status === 'pr_created' ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge 
                        className={cn(
                          "bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800",
                          change.pr_url && "cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-900/40"
                        )}
                        onClick={() => {
                          if (change.pr_url) {
                            window.open(change.pr_url, '_blank');
                          }
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        PR Created
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      {change.pr_url ? 'Click to view pull request' : 'Pull request URL not available'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <DeployToGitHubButton
                  yamlContent={change.yaml_content}
                  sourceType="chat_yaml_change"
                  sourceId={change.id}
                  defaultTitle={`[KestrelAI] ${change.file_type} ${change.resource_kind}/${change.resource_name || 'resource'}`}
                  defaultDescription={`YAML change from chat session${change.explanation ? `: ${change.explanation}` : ''}`}
                  className="text-xs"
                />
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyYamlToClipboard(change.yaml_content, change.id)}
                className="text-xs"
              >
                <Copy className="h-3 w-3 mr-1" />
                {isCopied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadYamlFile(change.yaml_content, change.file_path)}
                className="text-xs"
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
              {canUserApprove(change) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleApprove(change.id)}
                  className="text-xs"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Approve
                </Button>
              )}
              {canSafeApply(change) && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleApply(change.id)}
                  disabled={applyingChangeId === change.id}
                  className="text-xs"
                >
                  {applyingChangeId === change.id ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Play className="h-3 w-3 mr-1" />
                      Apply
                    </>
                  )}
                </Button>
              )}
              {change.approval_status === 'approved_no_safe_apply' && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled
                  className="text-xs"
                  title="Safe-apply is disabled for this cluster"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Safe-Apply Disabled
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Change Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Cluster:</span> {change.cluster_name}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Created by:</span> 
              <span>{change.user_email}</span>
              {change.user_role && (
                <Badge className={getRoleBadgeColor(change.user_role)}>
                  {getRoleIcon(change.user_role)}
                  <span className="ml-1">{formatRole(change.user_role)}</span>
                </Badge>
              )}
            </div>
            <div>
              <span className="font-medium">Created:</span> {formatDate(change.created_at)}
            </div>
          </div>

          {/* Approval Status */}
          <div className="space-y-2">
            <h5 className="font-medium text-foreground">Approval Status</h5>
            <div className="flex items-center gap-4">
              {/* Only show namespace owner approval for namespaced resources */}
              {change.resource_namespace && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="text-sm">Namespace Owner:</span>
                  {change.namespace_owner_approved_by ? (
                    <Badge className="bg-green-100 text-green-800">Approved</Badge>
                  ) : (
                    <Badge variant="outline">Pending</Badge>
                  )}
                </div>
              )}
              {/* Show ops admin approval if tenant requires it (server-computed) */}
              {change.requires_ops_admin_approval && (
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm">Platform Admin:</span>
                  {change.ops_admin_approved_by ? (
                    <Badge className="bg-green-100 text-green-800">Approved</Badge>
                  ) : (
                    <Badge variant="outline">Pending</Badge>
                  )}
                </div>
              )}
              {/* Show org admin approval if present */}
              {change.org_admin_approved_by && (
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4" />
                  <span className="text-sm">Org Admin:</span>
                  <Badge className="bg-zinc-100 text-zinc-800">Approved</Badge>
                </div>
              )}
              {change.applied_at && (
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4" />
                  <span className="text-sm">Applied:</span>
                  <Badge className="bg-zinc-100 text-zinc-800">
                    {formatDate(change.applied_at)}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {change.explanation && (
            <p className="text-sm text-muted-foreground">{change.explanation}</p>
          )}

          {/* YAML Content */}
          <Collapsible open={isExpanded} onOpenChange={() => toggleChangeExpansion(change.id)}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  View YAML Configuration
                </span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="space-y-4">
                {/* Toggle between single view and diff view for updates */}
                {change.existing_yaml && change.file_type === 'update' && (
                  <div className="flex justify-center">
                    <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                      <Button
                        variant={!showDiffView.has(change.id) ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setShowDiffView(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(change.id);
                          return newSet;
                        })}
                        className="text-xs h-7"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        New YAML
                      </Button>
                      <Button
                        variant={showDiffView.has(change.id) ? "default" : "ghost"}
                        size="sm"
                        onClick={() => toggleDiffView(change.id)}
                        className="text-xs h-7"
                      >
                        <GitCompare className="h-3 w-3 mr-1" />
                        Show Diff
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* YAML Content Display */}
                {change.existing_yaml && change.file_type === 'update' && showDiffView.has(change.id) ? (
                  // Unified diff view for updates
                  <div className="bg-muted p-3 rounded-md border font-mono text-sm overflow-x-auto max-h-96">
                    <DiffView oldText={change.existing_yaml} newText={change.yaml_content} />
                  </div>
                ) : (
                  // Single view (for creates, deletes, or when not in diff mode)
                  <pre className={cn(
                    "p-4 rounded-md border font-mono text-sm overflow-x-auto max-h-96",
                    change.file_type === 'delete' 
                      ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                      : change.file_type === 'create'
                      ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                      : "bg-muted"
                  )}>
                    <code className="text-foreground">
                      {change.file_type === 'delete' && change.existing_yaml 
                        ? change.existing_yaml 
                        : change.yaml_content}
                    </code>
                  </pre>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

                      {/* Application Error */}
            {change.application_error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Application failed:</strong> {change.application_error}
                </AlertDescription>
              </Alert>
            )}
            
            {/* Safe-Apply Disabled Warning */}
            {change.approval_status === 'approved_no_safe_apply' && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Safe-apply is disabled</strong> for this cluster. While this change is approved, it cannot be applied. Contact your administrator to enable safe-apply or apply the change manually.
                </AlertDescription>
              </Alert>
            )}
        </CardContent>
      </Card>
    );
  };

  const renderCodeChangeGroupCard = (group: CodeChangeGroup) => {
    const isMultiFile = group.changes.length > 1;

    return (
      <Card key={group.groupKey} className="border-l-4 border-l-zinc-400 overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-base font-mono truncate" title={group.repo_full_name}>
                  {isMultiFile
                    ? `${group.changes.length} files in ${group.repo_full_name}`
                    : group.changes[0].file_path
                  }
                </CardTitle>
                {!isMultiFile && group.changes[0].language && getLanguageBadge(group.changes[0].language)}
                {isMultiFile && (
                  <Badge variant="outline" className="text-xs border-zinc-300 dark:border-zinc-700">
                    {group.changes.length} files
                  </Badge>
                )}
                {group.application_status && group.application_status !== 'pr_created' && (
                  <Badge variant="outline" className={
                    group.application_status === 'applied' ? 'text-green-600' :
                    group.application_status === 'pr_created' ? 'text-emerald-600' :
                    group.application_status === 'failed' ? 'text-red-600' : 'text-muted-foreground'
                  }>
                    {group.application_status === 'applied' && <CheckCircle className="h-3 w-3 mr-1" />}
                    {group.application_status === 'pr_created' && <GitPullRequest className="h-3 w-3 mr-1" />}
                    {group.application_status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                    {group.application_status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                    <span className="capitalize">{group.application_status === 'pr_created' ? 'PR Created' : group.application_status}</span>
                  </Badge>
                )}
              </div>
              {!isMultiFile && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground truncate" title={group.repo_full_name}>
                    {group.repo_full_name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Lines {group.changes[0].line_start}–{group.changes[0].line_end}
                  </span>
                </div>
              )}
              {isMultiFile && (
                <span className="text-sm text-muted-foreground mt-1 block">{group.repo_full_name}</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
              {group.application_status !== 'pr_created' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleCreateCodeFixPR(group)}
                  disabled={creatingCodePR === group.groupKey}
                  className="text-xs"
                >
                  {creatingCodePR === group.groupKey ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Creating PR...
                    </>
                  ) : (
                    <>
                      <GitPullRequest className="h-3 w-3 mr-1" />
                      Create PR
                    </>
                  )}
                </Button>
              )}
              {group.application_status === 'pr_created' && group.pr_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(group.pr_url, '_blank')}
                  className="text-xs"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  View PR
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-1 min-w-0">
              <span className="font-medium flex-shrink-0">Repository:</span> <span className="truncate">{group.repo_full_name} ({group.repo_platform})</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium flex-shrink-0">Created by:</span>
              <span className="truncate">{group.user_email || 'Unknown'}</span>
            </div>
            <div>
              <span className="font-medium">Created:</span> {formatCloudDate(group.created_at)}
            </div>
          </div>

          {/* Render each file in the group */}
          {group.changes.map((change) => {
            const fileKey = `${group.groupKey}-${change.id}`;
            const isExpanded = expandedChanges.has(fileKey);
            const isCopied = copiedChange === change.id;

            return (
              <div key={change.id} className={cn("space-y-3", isMultiFile && "border rounded-md p-3 bg-muted/30")}>
                {/* File header (always visible for multi-file, explanation for single) */}
                {isMultiFile && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <Code className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                      <span className="text-sm font-medium truncate" title={change.file_path}>{change.file_path}</span>
                      {change.language && getLanguageBadge(change.language)}
                      <span className="text-xs text-muted-foreground">Lines {change.line_start}–{change.line_end}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyYamlToClipboard(change.fixed_code, change.id)}
                      className="text-xs flex-shrink-0"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      {isCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                )}

                {change.explanation && (
                  <div>
                    <h5 className="font-medium text-foreground mb-1 text-sm">{isMultiFile ? 'Fix:' : 'Explanation:'}</h5>
                    <p className="text-sm text-muted-foreground">{change.explanation}</p>
                  </div>
                )}

                <Collapsible open={isExpanded} onOpenChange={() => toggleChangeExpansion(fileKey)}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" size="sm" className="w-full justify-between">
                      <span className="flex items-center gap-2">
                        <GitCompare className="h-4 w-4" />
                        {isMultiFile ? `View Diff — ${change.file_path.split('/').pop()}` : 'View Diff'}
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <div className="bg-muted p-3 rounded-md border font-mono text-xs overflow-x-auto max-h-96">
                      <DiffView oldText={change.original_code || ''} newText={change.fixed_code || ''} gutterWidth="w-6" />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            );
          })}

          {/* Copy button for single-file changes (multi-file has per-file copy) */}
          {!isMultiFile && (
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyYamlToClipboard(group.changes[0].fixed_code, group.changes[0].id)}
                className="text-xs"
              >
                <Copy className="h-3 w-3 mr-1" />
                {copiedChange === group.changes[0].id ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const getCloudChangeTypeBadge = (changeType: string) => {
    switch (changeType) {
      case 'terraform':
        return <Badge className="bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800">Terraform</Badge>;
      case 'cloudformation':
        return <Badge className="bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800">CloudFormation</Badge>;
      case 'pulumi':
        return <Badge className="bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800">Pulumi</Badge>;
      case 'aws_cli':
        return <Badge className="bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800">AWS CLI</Badge>;
      case 'oci_cli':
        return <Badge className="bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800">OCI CLI</Badge>;
      default:
        return <Badge variant="secondary">{changeType}</Badge>;
    }
  };

  // Format cloud change date (from ISO string)
  const formatCloudDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render cloud change card
  const renderCloudChangeCard = (change: ChatCloudChange) => {
    const isExpanded = expandedChanges.has(change.id);
    const isCopied = copiedChange === change.id;

    return (
      <Card key={change.id} className="border-l-4 border-l-zinc-400">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-base">
                  {change.resource_type || 'Cloud Resource'}
                  {change.resource_identifier && ` - ${change.resource_identifier}`}
                </CardTitle>
                {getCloudChangeTypeBadge(change.change_type)}
                {change.application_status && change.application_status !== 'pr_created' && (
                  <Badge variant="outline" className={
                    change.application_status === 'applied' ? 'text-green-600' :
                    change.application_status === 'failed' ? 'text-red-600' : 'text-muted-foreground'
                  }>
                    {change.application_status === 'applied' && <CheckCircle className="h-3 w-3 mr-1" />}
                    {change.application_status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                    {change.application_status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                    <span className="capitalize">{change.application_status}</span>
                  </Badge>
                )}
              </div>
              {change.file_path && (
                <code className="text-xs text-muted-foreground font-mono mt-1 block">
                  {change.file_path}
                </code>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
              {/* Apply button for AWS CLI and OCI CLI commands */}
              {(change.change_type === 'aws_cli' || change.change_type === 'oci_cli') && change.application_status !== 'applied' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleApplyCloudChange(change.id)}
                  disabled={applyingCloudChangeId === change.id}
                  className="text-xs"
                >
                  {applyingCloudChangeId === change.id ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Play className="h-3 w-3 mr-1" />
                      Apply
                    </>
                  )}
                </Button>
              )}
              {/* Deploy IaC button for Terraform, Pulumi, and CloudFormation changes */}
              {['terraform', 'pulumi', 'cloudformation'].includes(change.change_type) && iacRepos.length > 0 && change.application_status !== 'pr_created' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleCreateCloudChangePR(change.id)}
                  disabled={creatingPR === change.id}
                  className="text-xs"
                >
                  {creatingPR === change.id ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Creating PR...
                    </>
                  ) : (
                    <>
                      <GitPullRequest className="h-3 w-3 mr-1" />
                      Deploy IaC
                    </>
                  )}
                </Button>
              )}
              {/* View PR button if PR already created */}
              {['terraform', 'pulumi', 'cloudformation'].includes(change.change_type) && change.application_status === 'pr_created' && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge 
                        className={cn(
                          "bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/20 dark:text-zinc-400 dark:border-zinc-800",
                          change.pr_url && "cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-900/40"
                        )}
                        onClick={() => {
                          // Use pr_url from the change object (persisted in DB)
                          // Fall back to prResult for immediate feedback after creation
                          const prUrl = change.pr_url || (prResult?.changeId === change.id ? prResult?.pr_url : null);
                          if (prUrl) {
                            window.open(prUrl, '_blank');
                          }
                        }}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        PR Created
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      {change.pr_url ? 'Click to view pull request' : 'Pull request URL not available'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyYamlToClipboard(change.content, change.id)}
                className="text-xs"
              >
                <Copy className="h-3 w-3 mr-1" />
                {isCopied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const ext = change.change_type === 'terraform' ? '.tf' :
                              change.change_type === 'cloudformation' ? '.yaml' :
                              change.change_type === 'pulumi' ? '.py' : '.sh';
                  const filename = `${change.resource_type || 'cloud-change'}${ext}`;
                  const blob = new Blob([change.content], { type: 'text/plain;charset=utf-8' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', filename);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
                className="text-xs"
              >
                <Download className="h-3 w-3 mr-1" />
                Download
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Change Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            {change.aws_account_id && (
              <div>
                <span className="font-medium">AWS Account:</span> {change.aws_account_id}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="font-medium">Created by:</span>
              <span className="truncate">{change.user_email || 'Unknown'}</span>
            </div>
            <div>
              <span className="font-medium">Created:</span> {formatCloudDate(change.created_at)}
            </div>
          </div>

          {change.explanation && (
            <p className="text-sm text-muted-foreground">{change.explanation}</p>
          )}

          {/* Content */}
          <Collapsible open={isExpanded} onOpenChange={() => toggleChangeExpansion(change.id)}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  View {change.change_type === 'terraform' ? 'Terraform' :
                        change.change_type === 'cloudformation' ? 'CloudFormation' :
                        change.change_type === 'pulumi' ? 'Pulumi' :
                        change.change_type === 'oci_cli' ? 'OCI CLI' : 'AWS CLI'} Content
                </span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="space-y-4">
                {/* Toggle between single view and diff view for updates with existing content */}
                {change.existing_content && change.file_type === 'update' && (
                  <div className="flex justify-center">
                    <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                      <Button
                        variant={!showDiffView.has(change.id) ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setShowDiffView(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(change.id);
                          return newSet;
                        })}
                        className="text-xs h-7"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        New Code
                      </Button>
                      <Button
                        variant={showDiffView.has(change.id) ? "default" : "ghost"}
                        size="sm"
                        onClick={() => toggleDiffView(change.id)}
                        className="text-xs h-7"
                      >
                        <GitCompare className="h-3 w-3 mr-1" />
                        Show Diff
                      </Button>
                    </div>
                  </div>
                )}

                {/* Content Display */}
                {change.existing_content && change.file_type === 'update' && showDiffView.has(change.id) ? (
                  // Unified diff view for updates
                  <div className="bg-muted p-3 rounded-md border font-mono text-sm overflow-x-auto max-h-96">
                    <DiffView oldText={change.existing_content} newText={change.content} />
                  </div>
                ) : (
                  // Single view (for creates, deletes, or when not in diff mode)
                  <pre className={cn(
                    "p-4 rounded-md border font-mono text-sm overflow-x-auto max-h-96 bg-muted"
                  )}>
                    <code className="text-foreground">{change.content}</code>
                  </pre>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Application Result */}
          {cloudApplyResult && cloudApplyResult.changeId === change.id && (
            <Alert 
              variant={cloudApplyResult.success ? 'default' : 'destructive'}
              className={cn(
                cloudApplyResult.success ? 'border-green-500/50 bg-green-500/10' :
                (cloudApplyResult.fix_attempted && cloudApplyResult.fixed_command) ? 'border-amber-500/50 bg-amber-500/10' : ''
              )}
            >
              {cloudApplyResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (cloudApplyResult.fix_attempted && cloudApplyResult.fixed_command) ? (
                <Wand2 className="h-4 w-4 text-amber-500" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <div className="ml-2">
                <h5 className="font-medium">
                  {cloudApplyResult.success 
                    ? 'Applied Successfully' 
                    : (cloudApplyResult.fix_attempted && cloudApplyResult.fixed_command)
                      ? 'Command Failed - AI Suggested Fix Available'
                      : 'Application Failed'}
                </h5>
                <p className="text-sm text-muted-foreground mt-1">{cloudApplyResult.message}</p>
                {cloudApplyResult.error_message && (
                  <p className="text-sm text-red-500 mt-1">{cloudApplyResult.error_message}</p>
                )}
                {cloudApplyResult.output && (
                  <pre className="mt-2 bg-muted/50 p-2 rounded text-xs overflow-x-auto max-h-32">
                    {cloudApplyResult.output}
                  </pre>
                )}

                {/* AI-suggested fix */}
                {cloudApplyResult.fix_attempted && cloudApplyResult.fixed_command && (
                  <div className="mt-4 space-y-3 border-t border-amber-500/30 pt-3">
                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-medium">
                      <Wand2 className="h-4 w-4" />
                      AI-Suggested Fixed Command
                    </div>
                    {cloudApplyResult.fix_explanation && (
                      <p className="text-sm text-muted-foreground">
                        {cloudApplyResult.fix_explanation}
                      </p>
                    )}
                    {cloudApplyResult.fix_changes_made && cloudApplyResult.fix_changes_made.length > 0 && (
                      <div className="text-sm">
                        <span className="font-medium">Changes made:</span>
                        <ul className="list-disc list-inside ml-2 text-muted-foreground">
                          {cloudApplyResult.fix_changes_made.map((c, i) => (
                            <li key={i}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <pre className="bg-muted p-3 rounded text-xs overflow-x-auto font-mono">
                      {cloudApplyResult.fixed_command}
                    </pre>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(cloudApplyResult.fixed_command!);
                          setCopiedChange(change.id + '-fixed');
                          setTimeout(() => setCopiedChange(null), 2000);
                        }}
                        className="text-xs"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        {copiedChange === change.id + '-fixed' ? 'Copied!' : 'Copy Fixed Command'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Review the suggested command above. You can copy and run it manually.
                    </p>
                  </div>
                )}

                {/* Cannot auto-fix message */}
                {cloudApplyResult.cannot_auto_fix && cloudApplyResult.fix_explanation && (
                  <div className="mt-3 p-3 bg-muted/50 rounded border border-red-500/20">
                    <p className="text-sm font-medium text-red-600 dark:text-red-400">Cannot automatically fix this issue</p>
                    <p className="text-sm text-muted-foreground mt-1">{cloudApplyResult.fix_explanation}</p>
                  </div>
                )}
              </div>
            </Alert>
          )}

          {/* PR Creation Result */}
          {prResult && prResult.changeId === change.id && (
            <Alert 
              variant={prResult.success ? 'default' : 'destructive'}
              className={cn(
                prResult.success ? 'border-zinc-500/50 bg-zinc-500/10' : ''
              )}
            >
              {prResult.success ? (
                <GitPullRequest className="h-4 w-4 text-zinc-500" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <div className="ml-2">
                <h5 className="font-medium">
                  {prResult.success ? 'Pull Request Created' : 'Failed to Create PR'}
                </h5>
                {prResult.success && prResult.pr_url && (
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(prResult.pr_url, '_blank')}
                      className="text-xs"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Pull Request
                    </Button>
                  </div>
                )}
                {prResult.error && (
                  <p className="text-sm text-red-500 mt-1">{prResult.error}</p>
                )}
              </div>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render namespace group
  const renderNamespaceGroup = (group: NamespaceGroup) => (
    <div key={group.namespace} className="space-y-3">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => toggleNamespaceExpansion(group.namespace)}
          className="flex items-center gap-2 p-2 h-auto"
        >
          {group.isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
          <div className="flex items-center gap-2">
            <NamespaceIcon className="h-4 w-4 text-zinc-500" />
            <span className="font-medium text-foreground">{group.namespace}</span>
            <Badge variant="secondary" className="text-xs">
              {group.changes.length} {group.changes.length === 1 ? 'change' : 'changes'}
            </Badge>
          </div>
        </Button>
      </div>
      
      {group.isExpanded && (
        <div className="space-y-4 pl-6">
          {group.changes.map(change => renderChangeCard(change))}
        </div>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <X className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters and Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* Change Type Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-foreground">Type:</label>
              <Select value={changeType} onValueChange={(value) => { setChangeType(value as 'kubernetes' | 'cloud' | 'code'); setPage(1); }}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kubernetes">
                    <div className="flex items-center gap-2">
                      <ShipWheel className="h-4 w-4 text-zinc-500" />
                      <span>Kubernetes</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="cloud">
                    <div className="flex items-center gap-2">
                      <Cloud className="h-4 w-4 text-zinc-500" />
                      <span>Cloud</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="code">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-zinc-500" />
                      <span>Code</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cluster Selector, Tab Filter, Tooltip - Only show for Kubernetes changes */}
            {changeType === 'kubernetes' && (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-foreground">Cluster:</label>
                  <Select value={selectedClusterId} onValueChange={handleClusterChange} disabled={clustersLoading}>
                    <SelectTrigger className="w-48">
                      <SelectValue>
                        {clustersLoading ? 'Loading...' : selectedClusterName}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clusters</SelectItem>
                      {clusters.map((cluster) => (
                        <SelectItem key={cluster.id} value={cluster.id}>
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              "w-2 h-2 rounded-full",
                              cluster.is_connected ? "bg-green-500" : "bg-gray-400"
                            )} />
                            {cluster.cluster_name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tab Filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-foreground">View:</label>
                  <Select value={activeTab} onValueChange={(value) => { setActiveTab(value as 'all' | 'my-reviews'); setPage(1); }}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-600" />
                          <span>All Changes</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="my-reviews">
                        <div className="flex items-center gap-2">
                          <Filter className="h-4 w-4 text-zinc-500" />
                          <span>My Reviews</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Filter explanation tooltip */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <div className="space-y-2 text-xs">
                      <div><strong>All Changes:</strong> Shows every YAML change in the system</div>
                      <div><strong>My Reviews:</strong> Changes specifically assigned to you for review</div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </div>

          {/* Statistics */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <span className="font-medium">{changeType === 'cloud' ? cloudChanges.length : changeType === 'code' ? codeChanges.length : stats.total}</span>
              <span>changes</span>
            </div>
            {changeType === 'kubernetes' && selectedClusterId !== 'all' && stats.namespaces > 0 && (
              <div className="flex items-center gap-1">
                <span className="font-medium">{stats.namespaces}</span>
                <span>namespaces</span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        {changeType === 'code' ? (
          // Show code changes grouped by chat message (source code fixes from connected repos)
          <div className="space-y-4">
            {codeChangeGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((group) => renderCodeChangeGroupCard(group))}

            {codeChangeGroups.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">No code changes found</h3>
                  <p className="text-muted-foreground">
                    Source code fixes from connected GitHub/GitLab repositories will appear here.
                  </p>
                </CardContent>
              </Card>
            )}
            {codeChangeGroups.length > PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, codeChangeGroups.length)} of {codeChangeGroups.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(codeChangeGroups.length / PAGE_SIZE)}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(Math.ceil(codeChangeGroups.length / PAGE_SIZE), p + 1))} disabled={page >= Math.ceil(codeChangeGroups.length / PAGE_SIZE)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        ) : changeType === 'cloud' ? (
          // Show cloud changes (Terraform, CloudFormation, AWS CLI)
          <div className="space-y-4">
            {cloudChanges.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((change) => renderCloudChangeCard(change))}

            {cloudChanges.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">No cloud changes found</h3>
                  <p className="text-muted-foreground">
                    Cloud changes (Terraform, CloudFormation, Pulumi, AWS CLI, OCI CLI) from chat sessions will appear here.
                  </p>
                </CardContent>
              </Card>
            )}
            {cloudChanges.length > PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, cloudChanges.length)} of {cloudChanges.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(cloudChanges.length / PAGE_SIZE)}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(Math.ceil(cloudChanges.length / PAGE_SIZE), p + 1))} disabled={page >= Math.ceil(cloudChanges.length / PAGE_SIZE)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        ) : selectedClusterId === 'all' ? (
          // Show flat list for all clusters
          <div className="space-y-4">
            {changes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((change) => renderChangeCard(change, activeTab === 'my-reviews' ? 'border-l-amber-500' : 'border-l-zinc-400'))}

            {changes.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {activeTab === 'my-reviews' ? 'No reviews pending' : 'No configuration changes found'}
                  </h3>
                  <p className="text-muted-foreground">
                    {activeTab === 'my-reviews'
                      ? 'There are no configuration changes awaiting your review.'
                      : 'Configuration changes from chat sessions will appear here.'}
                  </p>
                </CardContent>
              </Card>
            )}
            {changes.length > PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-sm text-muted-foreground">
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, changes.length)} of {changes.length}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Previous</Button>
                  <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(changes.length / PAGE_SIZE)}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(Math.ceil(changes.length / PAGE_SIZE), p + 1))} disabled={page >= Math.ceil(changes.length / PAGE_SIZE)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Show grouped by namespace for specific cluster
          <div className="space-y-6">
            {namespaceGroups.map(renderNamespaceGroup)}

            {namespaceGroups.length === 0 && changes.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <h3 className="text-lg font-medium text-foreground mb-2">No configuration changes found</h3>
                  <p className="text-muted-foreground">
                    {activeTab === 'my-reviews'
                      ? `No reviews pending for ${selectedClusterName}.`
                      : `No changes found for ${selectedClusterName}.`}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ChatChangelog; 